import React, { useMemo } from 'react';
import {
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
} from '@mui/material';

/** Parse Option like "range(-12, 0)" -> { min: -12, max: 0 } */
const parseRange = (opt) => {
  if (typeof opt !== 'string') return {};
  const m = opt.match(
    /^range\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\)$/i,
  );
  if (!m) return {};
  const min = Number(m[1]);
  const max = Number(m[2]);
  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : {};
};

const PredictiveFrostProtectionFormFields = ({
  sectionState, // object with current values for this section
  specification, // array of fields from spec (your "predictive_frost_protection" array)
  onSectionSubmit, // (nextSectionObject) => void
}) => {
  // Merge defaults from spec with current section state from parent
  const value = useMemo(() => {
    const out = {};
    for (const field of specification) {
      const k = field.Key;
      out[k] = sectionState?.[k] ?? field.DefaultValue;
    }
    return out;
  }, [specification, sectionState]);

  const updateField = (key, nextVal) => {
    // Emit the full updated section object
    onSectionSubmit({ ...value, [key]: nextVal });
  };

  return (
    <Grid container spacing={2}>
      {specification.map((field) => {
        const { Key, DisplayName, Type, Options } = field;
        const fieldValue = value[Key];

        if (Type === 'bool') {
          return (
            <Grid item xs={12} sm={12} key={Key}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!fieldValue}
                      onChange={(e) => updateField(Key, e.target.checked)}
                    />
                  }
                  label={DisplayName}
                />
              </FormGroup>
            </Grid>
          );
        }

        if (Type === 'float') {
          const { min, max } = parseRange(Options);
          return (
            <Grid item xs={12} sm={4} key={Key}>
              <TextField
                fullWidth
                variant="filled"
                label={DisplayName}
                type="number"
                value={fieldValue ?? ''} // keep controlled
                onChange={(e) => {
                  // Allow empty string during editing; otherwise parse number
                  const raw = e.target.value;
                  if (raw === '') {
                    updateField(Key, '');
                    return;
                  }
                  const num = Number(raw);
                  updateField(
                    Key,
                    Number.isFinite(num) ? num : (fieldValue ?? ''),
                  );
                }}
                InputProps={{ disableUnderline: true }}
                inputProps={{
                  ...(min !== undefined ? { min } : {}),
                  ...(max !== undefined ? { max } : {}),
                  step: 'any', // floats
                }}
                sx={{
                  '& .MuiFilledInput-root': {
                    borderRadius: 1,
                    overflow: 'hidden',
                  },
                }}
                onBlur={(e) => {
                  // On blur, coerce '' back to default if needed
                  if (e.target.value === '') {
                    updateField(Key, field.DefaultValue);
                  }
                }}
              />
            </Grid>
          );
        }

        // Unknown/unsupported types in this section
        return null;
      })}
    </Grid>
  );
};

export default PredictiveFrostProtectionFormFields;
